import axios from 'axios';

//const API_URL = 'http://localhost:5001/api';
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add token to every request automatically
// sessionStorage wins (impersonation tab) — falls back to localStorage (normal session)
api.interceptors.request.use((config) => {
  const token = sessionStorage.getItem('pwc_token') || localStorage.getItem('pwc_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle token expiry
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('pwc_token');
      localStorage.removeItem('pwc_user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const login = (data) => api.post('/auth/login', data);
export const register = (data) => api.post('/auth/register', data);
export const getMe = () => api.get('/auth/me');

// Users
export const getUsers = (params) => api.get('/users', { params });
export const getUser = (id) => api.get(`/users/${id}`);
export const updateUser = (id, data) => api.put(`/users/${id}`, data);
export const getHealers = () => api.get('/users/healers');
export const getPractitioners = () => api.get('/users/practitioners');
export const deleteUser = (id) => api.delete(`/users/${id}`);
export const getStats = () => api.get('/users/stats/overview');
export const updateHealerVisibility = (id, data) => api.put(`/users/${id}/healer-visibility`, data);
export const getNotifications = () => api.get('/users/notifications');
export const markNotificationsRead = () => api.put('/users/notifications/read');
export const getPendingApprovals = () => api.get('/users/pending-approvals');
export const impersonateUser = (id) => api.post(`/users/${id}/impersonate`);
export const approveUser = (id) => api.put(`/users/${id}/approve`);
export const rejectUser = (id) => api.put(`/users/${id}/reject`);

// Sessions
export const getSessions = () => api.get('/sessions');
export const getSession = (id) => api.get(`/sessions/${id}`);
export const createSession = (data) => api.post('/sessions', data);
export const updateSession = (id, data) => api.put(`/sessions/${id}`, data);
export const deleteSession = (id) => api.delete(`/sessions/${id}`);
export const getSessionParticipants = (id) => api.get(`/sessions/${id}/participants`);

// Healing Requests
export const getHealingRequests = () => api.get('/healing-requests');
export const createHealingRequest = (data) => api.post('/healing-requests', data);
export const assignHealer = (id, data) => api.put(`/healing-requests/${id}/assign`, data);
export const addSession = (id, data) => api.put(`/healing-requests/${id}/session`, data);
export const addFeedback = (id, data) => api.put(`/healing-requests/${id}/feedback`, data);
export const getTransferLog = (id) => api.get(`/healing-requests/${id}/transfer-log`);
export const reopenCase = (id, data) => api.put(`/healing-requests/${id}/reopen`, data);
export const returnPatient = (id, data) => api.put(`/healing-requests/${id}/return`, data);
export const returnToHealer = (id, data) => api.put(`/healing-requests/${id}/return-to-healer`, data);
export const dropPatient = (id) => api.put(`/healing-requests/${id}/drop`, { confirm: true });
export const earlyComplete = (id, data) => api.put(`/healing-requests/${id}/early-complete`, data);
export const requestFeedback = (id) => api.put(`/healing-requests/${id}/request-feedback`);
export const submitFeedback = (id, data) => api.put(`/healing-requests/${id}/submit-feedback`, data);
export const requestSessionFeedback = (id, sessionIndex) => api.put(`/healing-requests/${id}/sessions/${sessionIndex}/request-feedback`);
export const submitSessionFeedback = (id, sessionIndex, data) => api.put(`/healing-requests/${id}/sessions/${sessionIndex}/submit-feedback`, data);
export const recallSessionFeedback = (id, sessionIndex) => api.put(`/healing-requests/${id}/sessions/${sessionIndex}/recall-feedback`);
export const updatePayment = (id, data) => api.put(`/healing-requests/${id}/payment`, data);
export const generatePatientInvoice = (id) => api.post(`/healing-requests/${id}/generate-patient-invoice`);
export const raiseHealerInvoice = (id, data) => api.post(`/healing-requests/${id}/raise-healer-invoice`, data);
export const adminSettlement = (id, data) => api.post(`/healing-requests/${id}/admin-settlement`, data);
export const settleHealerPayment = (id, paymentId, data) => api.put(`/healing-requests/${id}/healer-payments/${paymentId}/settle`, data);

// Announcements
export const getAnnouncementRecipients = (roles) => api.get('/announcements/recipients', { params: { roles: roles.join(',') } });
export const sendAnnouncement = (data) => api.post('/announcements/send', data);
export const getAnnouncementHistory = (params) => api.get('/announcements/history', { params });

// Case Notes
export const addCaseNote = (id, data) => api.post(`/healing-requests/${id}/notes`, data);

// Patient Activities
export const addActivity = (id, data) => api.post(`/healing-requests/${id}/activities`, data);
export const updateActivity = (id, actId, data) => api.put(`/healing-requests/${id}/activities/${actId}`, data);
export const removeActivity = (id, actId) => api.delete(`/healing-requests/${id}/activities/${actId}`);
export const checkInActivity = (id, actId, data) => api.post(`/healing-requests/${id}/activities/${actId}/checkin`, data);

export const updateCaseTags = (id, tags) => api.put(`/healing-requests/${id}/tags`, { tags });
export const setFollowUp = (id, data) => api.put(`/healing-requests/${id}/follow-up`, data);
export const sendHealingReport = (id, data) => api.post(`/healing-requests/${id}/send-report`, data);
export const globalSearch = (q) => api.get('/users/search', { params: { q } });
export const getUserHealingRequests = (id) => api.get(`/users/${id}/healing-requests`);

// Audit Logs
export const getAuditLogs = (params) => api.get('/audit-logs', { params });
export const getAuditActors = () => api.get('/audit-logs/actors');

// Course Completions
export const getCourseCompletions = () => api.get('/course-completions');
export const submitCourseCompletion = (data) => api.post('/course-completions', data);
export const verifyCourseCompletion = (id, data) => api.put(`/course-completions/${id}/verify`, data);

// Practitioner Activities
export const getPractitionerActivities = () => api.get('/practitioner-activities');
export const createPractitionerActivity = (data) => api.post('/practitioner-activities', data);
export const updatePractitionerActivity = (id, data) => api.put(`/practitioner-activities/${id}`, data);
export const deletePractitionerActivity = (id) => api.delete(`/practitioner-activities/${id}`);
export const practitionerActivityCheckin = (id, data) => api.post(`/practitioner-activities/${id}/checkin`, data);

export default api;